import { useHistory } from "react-router";

import { Button, Icon, Link, Text } from "@alpacahq/ui/old";

import {
  useGetHYCAccountStatus,
  useGetHYCAprTierOffer,
} from "~/v2/api/hooks/useGetHighYieldCash";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getIsEligibleForHighYieldCash } from "../../util";
import { Layout } from "../Layout";

const getBenefits = (apyOffer: string) =>
  [
    {
      key: "apy",
      content: (
        <Text className="text-black text-xl">
          Earn {apyOffer}% APY<sup>*</sup> on your uninvested cash
        </Text>
      ),
    },
    {
      key: "fdic",
      content: (
        <Text className="text-black text-xl">
          Be protected by $1 million of FDIC insurance<sup>**</sup>
        </Text>
      ),
    },
    {
      key: "buying-power",
      content: (
        <Text className="text-black text-xl">
          Continue to use your funds as buying power, just like you do today
        </Text>
      ),
    },
  ] as const;

const DISCLOSURES_URL = "https://alpaca.markets/disclosures";

const HighYieldCashEnrollmentStartPage = () => {
  const history = useHistory();

  const { product, currentAccount } = useAccountContext({ liveOnly: true });

  const { hycApyString: hycApyOfferString } = useGetHYCAprTierOffer({
    accountID: currentAccount?.id,
    options: { enabled: !!currentAccount?.id },
  });

  const { hycStatus } = useGetHYCAccountStatus({
    accountID: currentAccount?.id,
    options: { enabled: !!currentAccount?.id },
  });

  const isEnrolled = hycStatus === "ACTIVE" || hycStatus === "PENDING_CHANGE";

  const isEligible = getIsEligibleForHighYieldCash({
    product,
    currency: currentAccount?.currency,
    accountStatus: currentAccount?.status,
  });

  const onCancelClicked = () => {
    history.push(Path.ROUTE_DASHBOARD);
  };

  const onContinueClicked = () => {
    isEligible && history.push(Path.ROUTE_HIGH_YIELD_ENROLL_AGREEMENT);
  };

  return (
    <Layout title="Enroll in High Yield Cash" showFAQ>
      <ul className="flex flex-col gap-5">
        {getBenefits(hycApyOfferString).map((benefit) => (
          <li
            key={benefit.key}
            className="flex items-center justify-start gap-2"
          >
            <Icon
              variant="filled"
              name="CheckBadge"
              className="text-yellow-400"
            />
            {benefit.content}
          </li>
        ))}
      </ul>

      <Text className="text-gray-800 text-sm">
        Alpaca Securities and its affiliates are not a bank. High-Yield Cash is
        not a bank product or a checking/savings account.
        <br />
        <br />
        <sup>*</sup>This rate is effective as of December 24, 2024, and is
        subject to change and may not be the applicable rate on the date this is
        read.
        <br />
        <br />
        <sup>**</sup>Alpaca Securities offers a cash management program pursuant
        to the FDIC Bank Sweep. Customer funds are treated differently and are
        subject to separate regulatory regimes depending on whether customer
        funds are held in their brokerage account or within the FDIC Bank Sweep.
        Specifically, Alpaca Securities is a member of the Securities Investor
        Protection Corporation (SIPC), which protects securities customers of
        its members up to $500,000 (including $250,000 for claims for cash). The
        Federal Deposit Insurance Corporation (FDIC) insures up to $250,000 per
        deposit against the failure of an FDIC member bank. Customer funds held
        in brokerage accounts are SIPC insured, but are not eligible for FDIC
        insurance coverage. Funds maintained in the FDIC Bank Sweep are intended
        to be eligible for pass-through FDIC insurance coverage, but are not
        subject to SIPC coverage. FDIC insurance coverage does not protect
        against the failure of Alpaca, Alpaca Securities, or any of its or their
        affiliates and/or malfeasance by any Alpaca or Alpaca Securities
        employee. Program banks that participate in the FDIC Bank Sweep are not
        members of SIPC and therefore funds held in the Program are not SIPC
        protected.
        <br />
        <br />
        See{" "}
        <Link href={DISCLOSURES_URL} className="text-blue-700">
          alpaca.markets/disclosures
        </Link>{" "}
        for important additional disclosures regarding Alpaca Securities
        brokerage offering as well as FDIC Bank Sweep terms and conditions.
      </Text>

      <div className="flex items-center justify-between gap-4">
        <Button
          variant="alternate"
          className="border-yellow-400"
          onClick={onCancelClicked}
        >
          {!isEligible || isEnrolled ? "Take Me Home" : "Cancel"}
        </Button>
        <Button
          onClick={onContinueClicked}
          disabled={!isEligible || isEnrolled}
        >
          {!isEligible ? "Ineligible" : isEnrolled ? "Enrolled" : "Continue"}
        </Button>
      </div>
    </Layout>
  );
};

export default HighYieldCashEnrollmentStartPage;
